// styled-components for the AI assistant. They read the app's styled-components
// theme so dark mode matches the rest of the UI. The light theme returns a
// nearly-empty object (see src/theming/themes.js), so every token has a sane
// light-mode fallback.
import styled, { keyframes } from 'styled-components';
import { TOP_BAR_HEIGHT } from '@src/constants';

import {
  Z_INDEX_DRAWER,
  Z_INDEX_FEEDBACK_MODAL,
  Z_INDEX_LAUNCHER,
  Z_INDEX_RESIZE_HANDLE,
} from './constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type P = any;

const ACCENT = '#2185d0';
const surfaceBg = (props: P) => props.theme?.modal?.backgroundColor || '#ffffff';
const surfaceColor = (props: P) => props.theme?.modal?.color || 'rgba(0, 0, 0, 0.87)';
const surfaceBorder = (props: P) => props.theme?.modal?.borderColor || '#d4d4d5';
const inputBg = (props: P) => props.theme?.input?.backgroundColor || '#ffffff';
const inputColor = (props: P) => props.theme?.input?.color || 'rgba(0, 0, 0, 0.87)';
const inputBorder = (props: P) => props.theme?.input?.borderColor || '#d4d4d5';
const assistantBubbleBg = (props: P) => props.theme?.message?.backgroundColor || '#f7f7f9';
const assistantBubbleColor = (props: P) => props.theme?.message?.color || 'rgba(0, 0, 0, 0.87)';
const errorBg = (props: P) => props.theme?.formikErrorMessage?.backgroundColor || '#ffecec';
const errorColor = (props: P) => props.theme?.formikErrorMessage?.color || '#9c2b2e';

export const Launcher = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: ${Z_INDEX_LAUNCHER};
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background-color: #1678c2;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

// Wraps the below-header app content and reserves space on the right for the open
// drawer so nothing is hidden behind it. Mirrors the drawer's transition timing
// (and disables it mid-drag) so the two move together.
export const ContentShell = styled.div<{ $open: boolean; $width: number; $resizing: boolean }>`
  margin-right: ${(props: P) => (props.$open ? props.$width : 0)}px;
  transition: ${(props: P) => (props.$resizing ? 'none' : 'margin-right 0.25s ease')};
`;

export const Drawer = styled.div<{ $open: boolean; $width: number; $resizing: boolean }>`
  position: fixed;
  top: ${TOP_BAR_HEIGHT}px;
  right: 0;
  z-index: ${Z_INDEX_DRAWER};
  width: ${(props: P) => props.$width}px;
  /* Mirrors MAX_WIDTH_RATIO in usePanelWidth, so the cap holds even in the frame
     between a viewport change and the JS re-clamp. */
  max-width: 40vw;
  height: calc(100vh - ${TOP_BAR_HEIGHT}px);
  display: flex;
  flex-direction: column;
  background-color: ${surfaceBg};
  color: ${surfaceColor};
  border-left: 1px solid ${surfaceBorder};
  box-shadow: -2px 0 16px rgba(0, 0, 0, 0.2);
  transform: translateX(${(props: P) => (props.$open ? '0' : '100%')});
  transition: ${(props: P) => (props.$resizing ? 'none' : 'transform 0.25s ease')};
`;

// Thin draggable bar on the drawer's left edge. Absolutely positioned so it
// straddles the border without affecting the flex layout of the panel body.
export const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  left: -3px;
  width: 6px;
  height: 100%;
  z-index: ${Z_INDEX_RESIZE_HANDLE};
  cursor: ew-resize;
  touch-action: none;
  background-color: transparent;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${ACCENT};
  }
`;

export const Header = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid ${surfaceBorder};
  background-color: #142b52;
  color: #ffffff;
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
`;

export const BetaBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${ACCENT};
  border: 1px solid ${ACCENT};
  border-radius: 9px;
  padding: 1px 7px;
`;

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SessionId = styled.span`
  font-size: 11px;
  opacity: 0.7;
`;

export const NewChatButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  color: inherit;
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  padding: 5px 8px;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
`;

export const ErrorBanner = styled.div`
  flex: 0 0 auto;
  padding: 8px 16px;
  background-color: ${errorBg};
  color: ${errorColor};
  font-size: 13px;
`;

export const PanelBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const MessageList = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const HistoryList = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
`;

export const HistoryRow = styled.button<{ $active: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  background: ${(props: P) => (props.$active ? assistantBubbleBg(props) : 'transparent')};
  border: none;
  border-bottom: 1px solid ${surfaceBorder};
  color: inherit;
  cursor: pointer;
  padding: 10px 16px;
  font: inherit;

  &:hover {
    background: ${assistantBubbleBg};
  }
`;

export const HistoryRowTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const HistoryPreview = styled.div`
  font-size: 13px;
  /* min-width: 0 so the ellipsis works inside the flex row rather than the preview
     pushing the badge off the end. */
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CurrentBadge = styled.span`
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #ffffff;
  background: ${ACCENT};
  border-radius: 9px;
  padding: 2px 7px;
`;

export const HistoryMeta = styled.div`
  font-size: 11px;
  opacity: 0.6;
  margin-top: 3px;
`;

export const LoadMoreButton = styled.button`
  flex: 0 0 auto;
  margin: 10px auto;
  background: transparent;
  border: 1px solid ${surfaceBorder};
  border-radius: 4px;
  color: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 6px 12px;

  &:hover:not(:disabled) {
    background: ${assistantBubbleBg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Sits above the (still-clickable) LoadMoreButton, so a failed page fetch reads as
// a retry rather than replacing the rows already loaded.
export const LoadMoreError = styled.div`
  flex: 0 0 auto;
  margin: 6px auto 0;
  text-align: center;
  font-size: 11px;
  color: ${errorColor};
`;

export const EmptyState = styled.div`
  margin: auto;
  text-align: center;
  font-size: 13px;
  opacity: 0.7;
  padding: 0 24px;
`;

export const WelcomeState = styled.div`
  margin: auto 0;
  font-size: 13px;
  line-height: 1.45;
  padding: 0 12px;

  a {
    color: ${ACCENT};
    text-decoration: underline;
  }
`;

export const WelcomeIntro = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  font-weight: 600;
`;

export const WelcomeLabel = styled.div`
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  opacity: 0.6;
`;

export const ExamplePromptList = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
`;

export const ExamplePrompt = styled.li`
  margin-bottom: 6px;
  padding: 6px 10px;
  border-left: 3px solid ${ACCENT};
  border-radius: 0 6px 6px 0;
  background-color: ${assistantBubbleBg};
  color: ${assistantBubbleColor};
`;

export const WelcomeCallout = styled.a`
  display: block;
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid ${ACCENT};
  border-radius: 8px;
  background-color: rgba(33, 133, 208, 0.08);
  font-size: 13px;
  font-weight: 600;

  && {
    text-decoration: none;
  }
  &:hover {
    background-color: rgba(33, 133, 208, 0.16);
  }
`;

export const WelcomeFooter = styled.div`
  font-size: 12px;
  opacity: 0.75;

  p {
    margin: 0 0 4px;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

export const Disclaimer = styled.div`
  flex: 0 0 auto;
  text-align: center;
  font-size: 11px;
  opacity: 0.7;
  padding: 0 12px 10px;
`;

export const BubbleRow = styled.div<{ $isUser: boolean }>`
  display: flex;
  justify-content: ${(props: P) => (props.$isUser ? 'flex-end' : 'flex-start')};
`;

export const Bubble = styled.div<{ $isUser: boolean }>`
  position: relative;
  max-width: 85%;
  padding: 10px 12px;
  /* The feedback bar (copy/rating buttons) is absolutely positioned over an
     assistant bubble's bottom-right corner; only assistant bubbles carry it, so
     only they reserve the extra room to keep it from covering the last line. */
  padding-bottom: ${(props: P) => (props.$isUser ? '10px' : '30px')};
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.45;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  background-color: ${(props: P) => (props.$isUser ? ACCENT : assistantBubbleBg(props))};
  color: ${(props: P) => (props.$isUser ? '#ffffff' : assistantBubbleColor(props))};

  /* The feedback bar is an overlay hidden until the bubble is hovered or one of
     its buttons receives keyboard focus; see FeedbackBar's $forceVisible for the
     case where it must stay visible mid-interaction regardless of hover. */
  &:hover .feedback-bar,
  &:focus-within .feedback-bar {
    opacity: 1;
    pointer-events: auto;
  }

  p {
    margin: 0 0 8px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  pre {
    background: rgba(0, 0, 0, 0.25);
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
  }
  code {
    font-family: monospace;
    font-size: 12px;
  }
  table {
    display: block; /* lets the table scroll horizontally when wider than the bubble */
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 12px;
  }
  th,
  td {
    border: 1px solid ${surfaceBorder};
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
    overflow-wrap: break-word; /* word-level breaks; overrides inherited 'anywhere' so columns keep natural word width */
    word-break: normal;
  }
  th {
    white-space: nowrap;
    font-weight: 600;
    background-color: ${(props: P) => props.theme?.table?.backgroundColor || 'rgba(0, 0, 0, 0.04)'};
  }
`;

const blink = keyframes`
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
`;

export const StreamingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 2px;
`;

export const Dot = styled.span<{ $delay: number }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: currentColor;
  opacity: 0.2;
  animation: ${blink} 1.2s infinite ease-in-out;
  animation-delay: ${(props: P) => props.$delay}s;
`;

export const InputBar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid ${surfaceBorder};
`;

export const TextArea = styled.textarea`
  flex: 1 1 auto;
  resize: none;
  max-height: 120px;
  font-family: inherit;
  font-size: 13px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${inputBorder};
  background-color: ${inputBg};
  color: ${inputColor};
`;

export const SendButton = styled.button`
  flex: 0 0 auto;
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  background-color: ${ACCENT};
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;

  &:disabled {
    background-color: #9aa0a6;
    cursor: not-allowed;
  }
`;

export const ToolCalls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const ToolChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: ${(props: P) => props.theme?.label?.backgroundColorAttached || '#e8e8e8'};
  color: ${(props: P) => props.theme?.label?.color || 'rgba(0, 0, 0, 0.8)'};
`;

export const ActionsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`;

export const Card = styled.div`
  border: 1px solid ${surfaceBorder};
  border-radius: 8px;
  padding: 10px;
  background-color: ${surfaceBg};
`;

export const CardSummary = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  opacity: 0.85;
`;

export const ActionRow = styled.div`
  &:not(:first-child) {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid ${surfaceBorder};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

export const ActionType = styled.span`
  font-weight: 700;
  font-size: 12px;
`;

export const Confidence = styled.span`
  font-size: 11px;
  opacity: 0.7;
  text-transform: uppercase;
`;

export const Description = styled.p`
  margin: 0 0 4px;
  font-size: 13px;
`;

export const Rationale = styled.p`
  margin: 0 0 6px;
  font-size: 12px;
  opacity: 0.8;
`;

export const CardNote = styled.p`
  margin: 0 0 6px;
  font-size: 12px;
  font-style: italic;
  opacity: 0.85;
`;

export const ExpandToggle = styled.button`
  background: transparent;
  border: none;
  color: ${ACCENT};
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 6px;
`;

export const Payload = styled.pre`
  background: rgba(0, 0, 0, 0.15);
  padding: 8px;
  border-radius: 6px;
  font-size: 11px;
  overflow-x: auto;
  margin: 0 0 8px;
`;

export const CardButtons = styled.div`
  display: flex;
  gap: 8px;
`;

export const ApplyButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background-color: #21ba45;
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    background-color: #9aa0a6;
    cursor: not-allowed;
  }
`;

export const RejectButton = styled.button`
  padding: 6px 12px;
  border: 1px solid ${surfaceBorder};
  border-radius: 6px;
  background-color: transparent;
  color: ${surfaceColor};
  font-size: 12px;
  cursor: pointer;
`;

export const ResultBanner = styled.div<{ $applied: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props: P) => (props.$applied ? '#21ba45' : '#db2828')};
`;

const POSITIVE = '#21ba45';
const NEGATIVE = '#db2828';
const NEUTRAL = ACCENT;

const variantColor = (variant: 'positive' | 'negative' | 'neutral') =>
  variant === 'positive' ? POSITIVE : variant === 'negative' ? NEGATIVE : NEUTRAL;

// Copy / thumbs-up / thumbs-down actions, overlaid on the assistant bubble's
// bottom-right corner. Hidden by default (see Bubble's :hover/:focus-within rule
// above) so the affordance stays subtle until the reader engages with it;
// $forceVisible keeps it showing while a comment form below is in progress,
// even if the pointer has moved off the bubble.
export const FeedbackBar = styled.div<{ $forceVisible?: boolean }>`
  position: absolute;
  bottom: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px;
  border-radius: 8px;
  background-color: ${surfaceBg};
  border: 1px solid ${surfaceBorder};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  opacity: ${(props: P) => (props.$forceVisible ? 1 : 0)};
  pointer-events: ${(props: P) => (props.$forceVisible ? 'auto' : 'none')};
  transition: opacity 0.15s ease;
`;

// A silent failure (no modal, no other UI) after a 👍 click would leave the user
// thinking the rating was saved, so this sits just above the bar -- forced visible
// like the bar itself, since a positive submission failing has nothing else to open.
export const FeedbackBarError = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 4px;
  padding: 3px 7px;
  border-radius: 4px;
  font-size: 11px;
  white-space: nowrap;
  background-color: ${errorBg};
  color: ${errorColor};
`;

// A single feedback button. $variant picks the "active" color; $active fills it
// in once the user has chosen that option (or, for the neutral copy button,
// while showing its transient "copied" state).
export const FeedbackButton = styled.button<{
  $variant: 'positive' | 'negative' | 'neutral';
  $active: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 5px;
  border: 1px solid
    ${(props: P) => (props.$active ? variantColor(props.$variant) : surfaceBorder(props))};
  background-color: ${(props: P) => (props.$active ? variantColor(props.$variant) : 'transparent')};
  color: ${(props: P) => (props.$active ? '#ffffff' : 'inherit')};
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${(props: P) => variantColor(props.$variant)};
  }
`;

// Dimmer behind the "bad response" comment modal. MessageFeedback portals this to
// `document.body` rather than rendering it inline: the Drawer has its own
// `transform` (for the slide-in animation), which makes it a containing block --
// and, inescapably, a new stacking context -- for any `position: fixed` descendant.
// Rendered inline, this overlay's z-index would be trapped inside that context and
// could never outrank a sibling like the app's own modal dimmer
// (Z_INDEX_MODAL_DIMMER in src/constants.js) no matter how high it's set. Portaled
// to the body, it competes on z-index directly against everything else in the app,
// which is what Z_INDEX_FEEDBACK_MODAL actually assumes.
export const FeedbackModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${Z_INDEX_FEEDBACK_MODAL};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.4);
`;

export const FeedbackModalCard = styled.div`
  width: 100%;
  max-width: 320px;
  border-radius: 10px;
  padding: 16px;
  background-color: ${surfaceBg};
  color: ${surfaceColor};
  border: 1px solid ${surfaceBorder};
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.3);
`;

export const FeedbackModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

export const FeedbackModalTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
`;

export const FeedbackModalClose = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

// Text box + submit, shown inside the feedback modal after a negative rating.
export const FeedbackForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FeedbackTextArea = styled.textarea`
  resize: none;
  min-height: 56px;
  font-family: inherit;
  font-size: 12px;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid ${inputBorder};
  background-color: ${inputBg};
  color: ${inputColor};
`;

export const FeedbackFormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

export const FeedbackSubmit = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background-color: ${ACCENT};
  color: #ffffff;
  font-size: 12px;
  cursor: pointer;

  &:disabled {
    background-color: #9aa0a6;
    cursor: not-allowed;
  }
`;

export const FeedbackThanks = styled.div`
  margin-top: 8px;
  font-size: 12px;
  opacity: 0.8;
`;
